import EventViewActions from 'flux/actions/EventViewActions';
import ReadOnlyActions from 'flux/actions/ReadOnlyActions';
import AlertActions from 'flux/actions/AlertActions';
import OnlineIndicatorActions from 'flux/actions/OnlineIndicatorActions';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import UserActions from 'flux/actions/UserActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import moment from 'moment-timezone';
import MessageConstants from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import RoleHelper from 'shared/lib/role';
import { fromJS } from 'immutable';

const READONLY = 'READONLY';

let team = null;
let event = null;
let show = null;
let user = null;

EventViewActions.populate.completed.listen((result) => (event = result));
CuesheetActions.populate.completed.listen((result) => (show = result.show));
ReadOnlyActions.clearShow.listen(() => (show = null));
UserActions.loadFromSession.completed.listen(
  (result) => (user = fromJS(result)),
);
TeamViewActions.populate.completed.listen((result) => (team = result));

let socketDisconnected = false;

OnlineIndicatorActions.setDisconnected.listen(
  () => (socketDisconnected = true),
);
OnlineIndicatorActions.setConnected.listen(() => (socketDisconnected = false));

function _isSocketDisconnected() {
  return socketDisconnected;
}

function _isDeletedEvent() {
  return event ? event.deleted : false;
}

function _isEventPastThreshold() {
  return event
    ? moment(event.endOn).isBefore(
        moment().hour(0).minute(0).subtract(7, 'days'),
      )
    : false;
}

function _isArchivedShow() {
  return show ? show.archived : false;
}

function _isSavedShow() {
  return show ? show.saved : false;
}

/**
 * Check if event is in read-only.
 *
 * @returns {Boolean}
 */
function isEventReadOnly() {
  if (event) {
    return _isDeletedEvent() || _isEventPastThreshold();
  }

  return false;
}

/**
 * Check if show is in read-only.
 *
 * @returns {Boolean}
 */
function isShowReadOnly() {
  if (event && show) {
    return isEventReadOnly() || _isArchivedShow() || _isSavedShow();
  }
  return false;
}

/**
 * Handler for shows or events that are in read only mode
 *
 * @params {STRING} message //Used by the alert modal.
 *
 * @returns {READONLY Constant}
 */
function _readOnlyHandler() {
  /**
   * Event is read only if:
   * 1. The event is deleted.
   * 2. The event is a past event. It ended before 12pm the day before today.
   *
   * Show is read only if:
   * 1. The show is deleted
   * 2. The show is a saved show. show.template == true
   */
  if (_isDeletedEvent()) {
    AlertActions.show(MessageConstants.readOnlyDeletedEventModal);
    return READONLY;
  } else if (_isArchivedShow()) {
    AlertActions.show(MessageConstants.readOnlyDeletedShowModal);
    return READONLY;
  } else if (_isSavedShow()) {
    AlertActions.show(MessageConstants.readOnlySavedShowModal);
    return READONLY;
  } else if (_isSocketDisconnected()) {
    AlertActions.show(MessageConstants.readOnlySocketDisconnectedModal);
  }

  //Return undefined for the shouldEmit method.
  return undefined;
}

function _getShouldEmit() {
  return {
    /**
     * Should action be emitted?
     *
     * @params readonly //If readonly is set to READONLY const, then don't allow
     * the action to emit.
     *
     * @returns {BOOLEAN}
     */
    shouldEmit(readonly) {
      if (readonly === READONLY) return false;
      return true;
    },
  };
}

function alertPastEventNotEditable() {
  const isTeamAdmin =
    user && team ? RoleHelper.hasTeamAdminRole(user, team._id) : false;
  if (_isEventPastThreshold() && !isTeamAdmin) {
    AlertActions.show(MessageConstants.readOnlyPastEventModal);
    return true;
  }
  return false;
}

/**
 * Action object for read only events/shows
 *
 * @param {Object} custom //Custom action object fields. For instance, sync.
 *
 * @returns {Object}
 */
export default {
  isEventReadOnly,
  isShowReadOnly,

  /**
   * Configuration object for actions that should be used with read-only
   * restrictions applied.
   *
   * @param {Object} [Optional] custom
   *
   * @returns {Object}
   */
  actionConfigFactory(custom = {}) {
    return Object.assign(
      {
        /**
         * What to do before the action is emitted?
         *
         * preEmit is fired before shouldEmit. Whatever premit returns
         * will be passed as arguments to shouldEmit.
         *
         * @returns undefined || READONLY constant
         */
        preEmit() {
          return _readOnlyHandler();
        },
      },
      _getShouldEmit(),
      custom,
    );
  },

  modalActionConfigFactory(custom = {}) {
    return Object.assign(
      {
        /**
         * What to do before the action is emitted?
         *
         * preEmit is fired before shouldEmit. Whatever premit returns
         * will be passed as arguments to shouldEmit.
         *
         * @returns undefined || READONLY constant
         */
        preEmit(type) {
          if (
            UIRegions.ShowForm === type ||
            UIRegions.GuestPass === type ||
            UIRegions.CuesheetItemNumber === type ||
            UIRegions.CuesheetItemTitle === type ||
            UIRegions.CuesheetItemDescription === type ||
            UIRegions.CuesheetItemMenu === type ||
            UIRegions.CuesheetItemDuration === type ||
            UIRegions.CuesheetItemStartTime === type ||
            UIRegions.CuesheetItemPresetDuration === type ||
            UIRegions.CuesheetItemCell === type ||
            UIRegions.CuesheetItemNote === type ||
            UIRegions.CuesheetColumnConfigMenu === type
          ) {
            //Use read-only handler for any of the above modals.
            return _readOnlyHandler();
          } else {
            //Return undefined for the shouldEmit method.
            return undefined;
          }
        },
      },
      _getShouldEmit(),
      custom,
    );
  },
  alertPastEventNotEditable,
};
